import axios from 'axios'
import NProgress from 'nprogress/nprogress'

export const state = {
  currentUser: null,
  token: getSavedState('auth.token'),
  tryingToLogin: false,
  tryingToSignup: false,
  tryingToVerifyPhone: false,
  successSignup: false,
  successForgotPassword: false,
  successCreatePassword: false,
  currentNotifications: [],
  notificationSettings: [],
  uuid: null,
}

export const mutations = {
  SET_TOKEN(state, newValue) {
    state.token = newValue
    saveState('auth.token', newValue)
    setDefaultAuthHeaders(state)
  },
  SET_CURRENT_USER(state, newValue) {
    state.currentUser = newValue
  },
  SET_CURRENT_USER_MESSAGE(state, message) {
    state.currentUser = {
      ...state.currentUser,
      ...message,
    }
  },
  SET_TRYING_TO_LOGIN(state, newValue) {
    state.tryingToLogin = newValue
    if (newValue) NProgress.start()
    else NProgress.done()
  },
  SET_TRYING_TO_SIGNUP(state, newValue) {
    state.tryingToSignup = newValue
  },
  SET_TRYING_TO_VERIFY_PHONE(state, newValue) {
    state.tryingToVerifyPhone = newValue
  },
  SET_SUCCESS_SIGNUP(state, newValue) {
    state.successSignup = newValue
  },
  SET_SUCCESS_FORGOT_PASSWORD(state, newValue) {
    state.successForgotPassword = newValue
  },
  SET_SUCCESS_CREATE_PASSWORD(state, newValue) {
    state.successCreatePassword = newValue
  },
  SET_NOTIFICATIONS(state, newValue) {
    state.currentNotifications = newValue
  },
  BOUGHT_PRODUCT(state, order) {
    state.currentUser.funds = state.currentUser.funds - order.total_price
  },
  SET_NOTIFICATION_NAMES(state, newValue) {
    state.notificationSettings = newValue
  },
  REMOVE_HUB(state, newValue) {
    state.currentUser.companies.forEach((element, index) => {
      if (element.id === newValue) {
        state.currentUser.companies.splice(index, 1)
      }
    })
  },
  REMOVE_OPEN_GROUP(state, newValue) {
    state.currentUser.open_groups.forEach((element, index) => {
      if (element.id === newValue) {
        state.currentUser.open_groups.splice(index, 1)
      }
    })
  },
  ADDREMOVE_TAG(state, params) {
    if (params.wasFollowed) {
      state.currentUser.tags.forEach((element, index) => {
        if (element.id === params.tag.id) {
          state.currentUser.tags.splice(index, 1)
        }
      })
    } else {
      state.currentUser.tags.push(params.tag)
    }
  },
  SET_DEFAULT_COMPANY(state, companyId) {
    state.currentUser.default_company_id = companyId
  },
  SET_UUID(state, uuid) {
    state.uuid = uuid
  },
  ACCEPT_INVITE_TO_HUB(state, invite) {
    state.currentUser.company_invites.forEach((element, index) => {
      if (element.id === invite.id) {
        state.currentUser.company_invites.splice(index, 1)
      }
    })
    state.currentUser.companies.push(invite.company)
  },
  REJECT_INVITE_TO_HUB(state, invite) {
    state.currentUser.company_invites.forEach((element, index) => {
      if (element.id === invite.id) {
        state.currentUser.company_invites.splice(index, 1)
      }
    })
  },
}

export const getters = {
  // Whether the user is currently logged in.
  loggedIn(state) {
    return !!state.token
  },
  userRetrieved(state) {
    return !!state.currentUser
  },
  isTryingToLogin(state) {
    return !!state.tryingToLogin
  },
  isTryingToSignup(state) {
    return !!state.tryingToSignup
  },
  isTryingToVerifyPhone(state) {
    return !!state.tryingToVerifyPhone
  },
  successfullySignup(state) {
    return !!state.tryingToSignup
  },
  canManageCompanies(state) {
    return () => {
      return state.currentUser.roles.find((role) => role.name === 'site-admin')
    }
  },
  canEditCompany(state, company) {
    return (company) => {
      if (!company) {
        return false
      }
      return (
        state.currentUser.roles.find((role) => role.name === 'site-admin') ||
        state.currentUser.roles.find(
          (role) => role.name === 'super-admin-of-company-' + company.id
        ) ||
        state.currentUser.roles.find(
          (role) => role.name === 'super-admin-of-company-' + company
        )
      )
    }
  },
  canEditGroup(state, group) {
    return (group) => {
      if (!group) {
        return false
      }

      return (
        state.currentUser.roles.find((role) => role.name === 'site-admin') ||
        state.currentUser.roles.find(
          (role) => role.name === 'admin-of-group-' + group.id
        ) ||
        state.currentUser.roles.find(
          (role) => role.name === 'admin-of-group-' + group
        )
      )
    }
  },
  canViewCompany(state, params) {
    return (params) => {
      return state.currentUser.companies.find(
        (company) => company.id === params.companyId
      )
    }
  },
}

export const actions = {
  // This is automatically run in `src/state/store.js` when the app
  // starts, along with any other actions named `init` in other modules.
  init({ state, dispatch }) {
    setDefaultAuthHeaders(state)
  },

  // Logs in the current user.
  logIn(
    { commit, dispatch, getters },
    { username, password, rememberMe } = {}
  ) {
    commit('SET_TRYING_TO_LOGIN', true)
    if (getters.loggedIn) return dispatch('validate')

    return axios
      .post('/api/v1/user/login', {
        username: username,
        password: password,
        remember_me: rememberMe,
      })
      .then((response) => {
        const user = response.data
        commit('SET_TOKEN', user.token)
        // commit('SET_TRYING_TO_LOGIN', false)
        return user
      })
      .catch((error) => {
        commit('SET_TRYING_TO_LOGIN', false)
        return error.response.data
      })
  },

  logInWithProvider(
    { commit, dispatch, getters },
    { name, email, imageUrl, idToken, userId, loginProvider } = {}
  ) {
    commit('SET_TRYING_TO_LOGIN', true)
    if (getters.loggedIn) return dispatch('validate')

    return axios
      .post('/api/v1/user/loginWithProvider', {
        name: name,
        email: email,
        image_url: imageUrl,
        id_token: idToken,
        user_id: userId,
        login_provider: loginProvider,
      })
      .then((response) => {
        const user = response.data
        commit('SET_TOKEN', user.token)
        commit('SET_TRYING_TO_LOGIN', false)
        return user
      })
      .catch((error) => {
        commit('SET_TRYING_TO_LOGIN', false)
        return error.response.data
      })
  },

  signUp({ commit, dispatch, getters }, { email, newPassword, fullName } = {}) {
    commit('SET_TRYING_TO_SIGNUP', true)
    if (getters.loggedIn) return dispatch('validate')
    return axios
      .post('/api/v1/user/register', {
        email: email,
        name: fullName,
        password: newPassword,
      })
      .then((response) => {
        const user = response.data
        commit('SET_SUCCESS_SIGNUP', true)
        commit('SET_TRYING_TO_SIGNUP', false)
        return user
      })
      .catch((error) => {
        commit('SET_TRYING_TO_SIGNUP', false)
        return error.response.data
      })
  },

  // Logs out the current user.
  logOut({ commit }) {
    commit('SET_TOKEN', null)
    commit('SET_CURRENT_USER', null)
  },

  forgotPassword({ commit, dispatch, getters }, { email } = {}) {
    commit('SET_TRYING_TO_LOGIN', true)
    if (getters.loggedIn) return dispatch('validate')

    return axios
      .post('/api/v1/user/forgotpassword', {
        email: email,
      })
      .then((response) => {
        commit('SET_TRYING_TO_LOGIN', false)
        commit('SET_SUCCESS_FORGOT_PASSWORD', true)
        return true
      })
      .catch((error) => {
        commit('SET_TRYING_TO_LOGIN', false)
        return error.response.data
      })
  },

  createPassword(
    { commit, dispatch, getters },
    { password, confirmPassword, token } = {}
  ) {
    commit('SET_TRYING_TO_LOGIN', true)
    if (getters.loggedIn) return dispatch('validate')

    return axios
      .post('/api/v1/user/createpassword', {
        password: password,
        password_confirm: confirmPassword,
        token: token,
      })
      .then((response) => {
        commit('SET_TRYING_TO_LOGIN', false)
        commit('SET_SUCCESS_CREATE_PASSWORD', true)
        return true
      })
      .catch((error) => {
        commit('SET_TRYING_TO_LOGIN', false)
        return error.response.data
      })
  },

  tryingToLoginError({ commit }) {
    // commit('SET_TRYING_TO_LOGIN', false)
  },

  tryingToSignupError({ commit }) {
    // commit('SET_TRYING_TO_SIGNUP', false)
  },

  // Validates the current user's token and refreshes it
  // with new data from the API.
  validate({ commit, dispatch, state }, fromLogin) {
    if (!state.token) return Promise.resolve(null)
    if (fromLogin) {
      commit('SET_TRYING_TO_LOGIN', true)
    }
    return axios
      .get('/api/v1/user/getByToken')
      .then((response) => {
        const user = response.data
        commit('SET_CURRENT_USER', user)
        dispatch('getNotifications')
        dispatch('getNotificationSettings')
        return user
      })
      .catch((error) => {
        if (
          error.response &&
          (error.response.status === 401 || error.response.status === 400)
        ) {
          commit('SET_CURRENT_USER', null)
          commit('SET_TOKEN', null)
        } else {
          console.warn(error)
        }
        return null
      })
  },

  getNotifications({ commit, state }) {
    if (!state.token) return Promise.resolve(null)
    return axios
      .get('/api/v1/user/getNotifications')
      .then((response) => {
        const notifications = response.data
        commit('SET_NOTIFICATIONS', notifications)
        return notifications
      })
      .catch((error) => {
        if (
          error.response &&
          (error.response.status === 401 || error.response.status === 400)
        ) {
          commit('SET_NOTIFICATIONS', null)
        } else {
          console.warn(error)
        }
        return null
      })
  },

  setAllNotificationRead({ commit, state }) {
    if (!state.token) return Promise.resolve(null)

    return axios
      .post('/api/v1/user/setAllNotificationRead')
      .then((response) => {
        const notifications = response.data
        commit('SET_NOTIFICATIONS', notifications)
        return notifications
      })
      .catch((error) => {
        if (
          error.response &&
          (error.response.status === 401 || error.response.status === 400)
        ) {
          commit('SET_NOTIFICATIONS', null)
        } else {
          console.warn(error)
        }
        return null
      })
  },

  setNotificationRead({ commit, state }, { notificationId }) {
    if (!state.token) return Promise.resolve(null)

    return axios
      .post('/api/v1/user/setNotificationRead', {
        notification_id: notificationId,
      })
      .then((response) => {
        const notifications = response.data
        commit('SET_NOTIFICATIONS', notifications)
        return notifications
      })
      .catch((error) => {
        if (
          error.response &&
          (error.response.status === 401 || error.response.status === 400)
        ) {
          commit('SET_NOTIFICATIONS', null)
        } else {
          console.warn(error)
        }
        return null
      })
  },

  getNotificationSettings({ commit, state }) {
    if (!state.token) return Promise.resolve(null)
    return axios
      .get('/api/v1/get_notification_names')
      .then((response) => {
        const notificationSettings = response.data
        commit('SET_NOTIFICATION_NAMES', notificationSettings)
        return notificationSettings
      })
      .catch((error) => {
        if (
          error.response &&
          (error.response.status === 401 || error.response.status === 400)
        ) {
          commit('SET_NOTIFICATION_NAMES', null)
        } else {
          console.warn(error)
        }
        return null
      })
  },

  search({ commit, state, rootState }, { term }) {
    return axios.get(`/api/v1/search/${term}`).then((response) => {
      return response.data
    })
  },
  updateUser({ commit, state, rootState }, { data }) {
    return axios
      .post(`/api/v1/user/${state.currentUser.id}`, data)
      .then(({ data }) => {
        commit('SET_CURRENT_USER_MESSAGE', data)
        return data
      })
  },

  verifyPhone(
    { commit, dispatch, getters },
    { phonePrefix, phoneNumber } = {}
  ) {
    commit('SET_TRYING_TO_VERIFY_PHONE', true)

    return axios
      .post('/api/v1/user/verifyphone', {
        phone_prefix: phonePrefix,
        phone_number: phoneNumber,
      })
      .then((response) => {
        commit('SET_TRYING_TO_VERIFY_PHONE', false)
        commit('SET_UUID', response.data.uuid)
        return response.data
      })
  },

  verifyCode({ commit, dispatch, getters }, { smsCode } = {}) {
    commit('SET_TRYING_TO_VERIFY_PHONE', true)

    return axios
      .post('/api/v1/user/verifycode', {
        sms_code: smsCode,
        uuid: state.uuid,
      })
      .then((response) => {
        commit('SET_TRYING_TO_VERIFY_PHONE', false)
        return response.data
      })
  },
  resendCode({ commit, dispatch, getters }) {
    commit('SET_TRYING_TO_VERIFY_PHONE', true)

    return axios
      .post('/api/v1/user/resendcode', {
        uuid: state.uuid,
      })
      .then((response) => {
        commit('SET_TRYING_TO_VERIFY_PHONE', false)
        return response.data
      })
  },
  skipPhone({ commit, dispatch, getters }) {
    commit('SET_TRYING_TO_VERIFY_PHONE', true)

    return axios.get('/api/v1/user/skipverifyphone').then((response) => {
      commit('SET_TRYING_TO_VERIFY_PHONE', false)
      return response.data
    })
  },
  loginByCode({ commit, dispatch, getters }, { smsCode } = {}) {
    commit('SET_TRYING_TO_LOGIN', true)

    return axios
      .post('/api/v1/user/loginbycode', {
        sms_code: smsCode,
        uuid: state.uuid,
      })
      .then((response) => {
        const user = response.data
        commit('SET_TOKEN', user.token)
        commit('SET_TRYING_TO_LOGIN', false)
        return user
      })
  },
  boughtProduct({ commit, dispatch, getters }, { order } = {}) {
    commit('BOUGHT_PRODUCT', order)
  },
  leaveHub({ commit, dispatch, getters }, { hubId }) {
    return axios
      .post(`/api/v1/user/leaveHub`, {
        hubId: hubId,
      })
      .then((response) => {
        commit('REMOVE_HUB', hubId)
        return response.data
      })
  },
  leaveOpenGroup({ commit, dispatch, getters }, { groupId }) {
    return axios
      .post(`/api/v1/user/leaveGroup`, {
        groupId: groupId,
      })
      .then((response) => {
        commit('REMOVE_OPEN_GROUP', groupId)
        return response.data
      })
  },
  toggleFollowTag({ commit, dispatch, getters }, params) {
    commit('ADDREMOVE_TAG', params)
  },
  setDefaultCompany({ commit, dispatch, getters }, { companyId }) {
    return axios
      .post(`/api/v1/user/setDefaultCompany`, {
        companyId: companyId,
      })
      .then((response) => {
        commit('SET_DEFAULT_COMPANY', companyId)
        return response.data
      })
  },
  acceptedHubInvite({ commit, dispatch, getters }, params) {
    commit('ACCEPT_INVITE_TO_HUB', params.invite)
  },
  rejectedHubInvite({ commit, dispatch, getters }, params) {
    commit('REJECT_INVITE_TO_HUB', params.invite)
  },
}

// ===
// Private helpers
// ===

function getSavedState(key) {
  return JSON.parse(window.localStorage.getItem(key))
}

function saveState(key, state) {
  window.localStorage.setItem(key, JSON.stringify(state))
}

function setDefaultAuthHeaders(state) {
  axios.defaults.headers.common.Authorization = state.token
    ? `Bearer ${state.token}`
    : ''
}
